import { useEffect, useMemo, useRef, useState } from "react"
import { useDispatch, useSelector } from "react-redux"
import { useTranslation } from "react-i18next"

import wsClient from "src/wsClient"
import { selectGameConfig } from "src/store/common"
import { addMessage } from "src/store/messages"
import {
  selectCityMainBuildingLevel,
  selectCityProductionQueue,
  selectCityWorkers,
  selectCurrentPlayer,
  selectIsCurrentPlayerReady,
  selectResearchLevels,
  selectPlayerFood,
  selectValidUnitsForPlayer,
} from "src/store/game"

import ModalService from "src/modals"
import UnitItem from "src/components/UnitItem"
import { consumptionMultiplier, discountedOrderCost, discountedUnitCost } from "src/utils/gameModifiers"
import { affordableNowFor, productionCapacityFor } from "src/utils/productionCapacity"
import { foodIcon } from "src/utils/resources"
import { focusFirstFieldOnTab, tabBetweenFields } from "src/utils/countFields"

import "./ProductionPanel.css"

// Humans spend workers held on this cell (COST_IN_WORKERS); zombies spend the player-level meat
// pool (COST). Different currencies — never add them together.
const costKeyFor = (team) => (team === "human" ? "COST_IN_WORKERS" : "COST")
const configKeyFor = (team) => (team === "human" ? "human_config" : "zombie_config")
// Tab walks the count fields, past the sliders between them (`utils/countFields`).
const tabBetweenCounts = tabBetweenFields(".production-unit-number")

/* How long the whole order takes, read at a glance (owner, 2026-10-03): green for one round, orange
   for two to ten, red past ten. Past ten is allowed — the colour is the warning, not a wall. */
export const durationTone = (rounds) => (rounds == null ? "" : rounds <= 1 ? "good" : rounds <= 10 ? "warn" : "bad")

const ProductionPanel = ({ city }) => {
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const player = useSelector(selectCurrentPlayer)
  const gameConfig = useSelector(selectGameConfig)
  const validUnits = useSelector(selectValidUnitsForPlayer)
  const queuedUnits = useSelector((state) => selectCityProductionQueue(state, city))
  const buildingLevel = useSelector((state) => selectCityMainBuildingLevel(state, city))
  const availableWorkers = useSelector((state) => selectCityWorkers(state, city))
  const availableFood = useSelector(selectPlayerFood)
  const isReady = useSelector(selectIsCurrentPlayerReady)
  const researchLevels = useSelector(selectResearchLevels)

  // The queue is server state; the inputs are a draft of it. Re-sync whenever the server's queue
  // changes so a broadcast after `user_enqueued_troops` is reflected rather than overwritten.
  const [draftQueue, setDraftQueue] = useState(queuedUnits)
  const queueSignature = JSON.stringify(queuedUnits)
  useEffect(() => setDraftQueue(queuedUnits), [queueSignature]) // eslint-disable-line react-hooks/exhaustive-deps

  // The first Tab in the city sheet goes to the first count (`utils/countFields`).
  const panelRef = useRef(null)
  const hasPanel = Boolean(player && gameConfig) && buildingLevel !== 0
  useEffect(() => (hasPanel ? focusFirstFieldOnTab(panelRef.current, ".production-unit-number") : undefined), [hasPanel])

  const team = player?.team
  const troopConfig = gameConfig?.[configKeyFor(team)]?.TROOPS
  const costKey = costKeyFor(team)

  const { totalCost, productionCapacity, upkeep } = useMemo(() => {
    // Military technology makes a human's units cheaper, and `build_from_command_center` charges
    // the discounted price. Quoting list price hid the research the player had bought.
    const listPrice = Object.entries(draftQueue).reduce((sum, [unitType, number]) => {
      return sum + (troopConfig?.[unitType]?.[costKey] || 0) * (Number(number) || 0)
    }, 0)
    // Off the whole order, as the server charges it (2026-09-14), not body by body.
    const cost = discountedOrderCost({ listPrice, gameConfig, team, researchLevels })
    // Outbreak raises the hatchery's output and the panel did not know, so a zombie who had bought it
    // was shown the un-researched number and warned their queue would carry over when it would not.
    // What the order eats a round once it stands (owner, 2026-10-03: "+50 food icon, representing
    // how much food this stack will be consuming if built"): each unit's `CONSUMPTION` times the
    // race's multiplier, applied to the whole bill and truncated, as `apply_troops_consumption`
    // bills it (`foodOutlook.foodUpkeepFor` does the same for the standing army).
    const eaten = Object.entries(draftQueue).reduce(
      (sum, [unitType, number]) => sum + (troopConfig?.[unitType]?.CONSUMPTION || 0) * (Number(number) || 0),
      0
    )
    return {
      totalCost: cost,
      productionCapacity: productionCapacityFor({ gameConfig, team, buildingLevel, researchLevels }),
      upkeep: Math.trunc(eaten * consumptionMultiplier({ gameConfig, race: player?.race })),
    }
  }, [draftQueue, troopConfig, costKey, gameConfig, team, buildingLevel, researchLevels, player?.race])

  if (!player || !gameConfig) return null

  // Level 0 is a normal state, not an error — the backend rejects it with BuildingNotOperational.
  if (buildingLevel === 0) {
    return (
      <div className="production-panel">
        <p className="production-blocked">
          {t("city.production.notBuilt", {
            building: team === "human" ? t("city.buildings.commandCenter") : t("city.buildings.hatchery"),
          })}
        </p>
      </div>
    )
  }

  const availableResource = team === "human" ? availableWorkers : availableFood
  const resourceLabel = team === "human" ? t("city.production.workersHere") : t("city.production.meat")
  // What the queue, the cap and the per-round pace are all counted in: a human's queue is paid in
  // the workers standing on this cell, the horde's out of the player's meat. It is the glyph and
  // not the word (owner, 2026-09-10), the call R32 already made for the worker's face over
  // "trab./rd" — so the locales hold 🥩 and 👷 here rather than a noun to decline.
  const resourceUnit = team === "human" ? t("city.production.workersShort") : t("city.production.meatShort")
  const isAffordable = totalCost <= availableResource
  // How many rounds the whole queue takes at this building's pace (owner's R33): "carries over to
  // the next round" said that it would not finish, and not when it would.
  const roundsToFinish = productionCapacity > 0 ? Math.max(1, Math.ceil(totalCost / productionCapacity)) : null

  const setUnitNumber = (unitType, number) =>
    setDraftQueue((current) => ({ ...current, [unitType]: Math.max(0, Number(number) || 0) }))

  const confirm = () => {
    // The backend action *replaces* the queue, so always send the full desired state. Zeroes are
    // dropped so that clearing every unit sends `{}`, which is how the queue is emptied.
    const troops = Object.fromEntries(Object.entries(draftQueue).filter(([, number]) => Number(number) > 0))
    // No toast (owner, 2026-10-03: the "queue set" message can go): the order's chips appear under
    // the army as the broadcast lands, which is the answer. A refusal still arrives as its own toast.
    wsClient.send("game", { action: "enqueue_troops", column: city.col, row: city.row, troops })
  }

  /* Space sets the queue straight after a slider was dragged or a count typed (owner, 2026-09-29),
     the same way it sends a march from the march picker: neither control has a use for a space.
     Only from those two — Space on a button here still presses that button. */
  const confirmOnSpace = (event) => {
    if (event.key !== " ") return
    const target = event.target
    if (target.type !== "range" && !target.classList?.contains("production-unit-number")) return
    event.preventDefault()
    if (isReady) return
    confirm()
  }

  // CLEAR empties the queue on the server too (owner, 2026-10-03: "LIMPIAR should automatically
  // save the queue"): a cleared draft nobody sets left the old order standing and building.
  const clear = () => {
    setDraftQueue({})
    const sent = wsClient.send("game", { action: "enqueue_troops", column: city.col, row: city.row, troops: {} })
    if (sent !== false) dispatch(addMessage({ text: t("city.production.queueCleared"), type: "success" }))
  }
  const savedTotal = Object.values(queuedUnits || {}).reduce((total, unit) => total + (Number(unit) || 0), 0)
  const queuedTotal = Object.values(draftQueue).reduce((total, number) => total + (Number(number) || 0), 0)
  // What one of each costs with the research discount applied — the slider's currency. A share of
  // the order and so fractional (a soldier is 0.9 of a worker at one level): the server rounds the
  // order, never the body.
  const effectiveCosts = Object.fromEntries(
    validUnits.map((unitType) => [
      unitType,
      discountedUnitCost({ listPrice: troopConfig?.[unitType]?.[costKey], gameConfig, team, researchLevels }),
    ])
  )

  return (
    <div className="production-panel" ref={panelRef} onKeyDown={confirmOnSpace}>
      <div className="production-head">
        <span className="section-label">{t("city.production.queue")}</span>
        <span className="production-rule" />
        {/* The pace alone (owner, 2026-10-03: "remove the 75 / 8000 workers. Just keep the workers
            that can convert per round"), in the currency the ledger charges in: an unlabelled
            "2000 / rd" reads as two thousand bodies a round. */}
        <span className="production-capacity">
          {t("city.production.pace", { amount: productionCapacity, resource: resourceUnit })}
        </span>
      </div>

      <div className="production-units">
        {validUnits.map((unitType) => {
          const queued = Number(draftQueue[unitType]) || 0
          // The slider reaches what the cell can pay for *now* — the workers standing here (or the
          // meat pool), after the rest of the draft, at the discounted price (owner, 2026-09-07).
          // S16 had sent it to the queue cap, ten rounds of workers, and a bar that wide could not
          // be placed. Queueing past this round is what the number field is for: it takes anything
          // up to the queue cap, and the slider's thumb simply sits at its end.
          const effectiveUnitCost = effectiveCosts[unitType]
          const sliderMax = affordableNowFor({
            unitType,
            costs: effectiveCosts,
            queue: draftQueue,
            available: availableResource,
          })
          const fill = sliderMax > 0 ? Math.min(100, (queued / sliderMax) * 100) : queued > 0 ? 100 : 0
          const unitName = t(`city.units.${unitType}`, { defaultValue: unitType })
          // A row, not a <label>: the name is a button now, and a label labels the first labelable
          // thing inside it — which would have named the button after the whole row and left the
          // slider unnamed. The two fields carry the unit's name themselves.
          return (
            <div className={`production-unit${queued > 0 ? " active" : ""}`} key={unitType}>
              <span className="production-unit-name">
                {/* The name opens the unit's sheet (owner, 2026-10-03). */}
                <button
                  type="button"
                  className="production-unit-info"
                  data-unit-info={unitType}
                  onClick={() => ModalService.show("UnitModal", { unitType })}
                >
                  {unitName}
                </button>
                {/* What one of them costs (owner's Q8): the exchange is not 1:1, and the player
                    deciding a mix needs the rate, not just the total at the bottom. */}
                <span className="production-unit-cost">
                  {t("city.production.perUnitCost", {
                    cost: Math.round(effectiveUnitCost * 100) / 100,
                    resource: team === "human" ? t("city.production.workersShort") : t("city.production.meatShort"),
                  })}
                </span>
              </span>
              <input
                type="range"
                aria-label={unitName}
                min="0"
                max={Math.max(sliderMax, queued)}
                step="1"
                disabled={isReady}
                value={queued}
                onChange={(e) => setUnitNumber(unitType, e.target.value)}
                style={{ "--fill": `${fill}%` }}
              />
              {/* Text with a numeric keypad, not type="number" — see AttackModal (R29). */}
              <input
                type="text"
                inputMode="numeric"
                pattern="[0-9]*"
                className="production-unit-number"
                aria-label={unitName}
                data-unit={unitType}
                disabled={isReady}
                value={queued}
                onKeyDown={tabBetweenCounts}
                onChange={(e) => setUnitNumber(unitType, e.target.value)}
              />
            </div>
          )
        })}
      </div>

      <div className="production-ledger">
        <div className="production-ledger-row">
          <span>{t("city.production.cost")}</span>
          <span className={isAffordable ? "" : "production-warning"}>
            <b>{totalCost}</b> / {availableResource} {resourceLabel}
          </span>
        </div>
        <div className="production-ledger-row">
          <span>{t("city.production.duration")}</span>
          <span className={`production-duration ${durationTone(roundsToFinish)}`} data-testid="production-duration">
            {roundsToFinish != null ? t("city.production.rounds", { count: roundsToFinish }) : t("city.production.carriesOver")}
          </span>
        </div>
        <div className="production-ledger-row">
          <span>{t("city.production.consumption")}</span>
          <span data-testid="production-consumption">
            +{upkeep} {foodIcon(team)} {t("city.production.perRoundShort")}
          </span>
        </div>
      </div>

      <div className="production-actions">
        {/* Affordability no longer gates the button (S16): the queue is a standing order the
            server builds as the city can pay, round after round, and it refuses nothing at
            enqueue time. The cost line still turns amber when this round cannot cover it. */}
        <button className="production-confirm" data-action="set-queue" onClick={confirm} disabled={isReady}>
          {isReady ? t("city.production.lockedIn") : t("city.production.setQueue")}
        </button>
        <button className="quiet production-clear" onClick={clear} disabled={isReady || (queuedTotal === 0 && savedTotal === 0)}>
          {t("city.production.clear")}
        </button>
      </div>
    </div>
  )
}

export default ProductionPanel
